/**
 * NIGHTBOUND - application shell.
 *
 * Routing is *derived from game state*, never trusted to the client's URL:
 *
 *   loading        -> boot screen
 *   anonymous      -> title + auth
 *   no creature    -> starter selection
 *   has creature   -> the game (shelter / stores / adventure / pit / settings)
 *
 * Audio is opt-in and only starts after a real user gesture. Reduced motion is
 * mirrored from the account setting onto the document root.
 */

import { useEffect, useRef } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { GameProvider, useGame } from './state/GameProvider'
import { audio } from './audio/AudioManager'
import { TopNav } from './components/TopNav'
import { BottomNav } from './components/BottomNav'
import { DebugPanel } from './components/DebugPanel'
import { ErrorBanner, ToastStack } from './components/ToastStack'
import { LoadingScreen } from './components/LoadingScreen'
import { TitleScreen } from './screens/TitleScreen'
import { AuthScreen } from './screens/AuthScreen'
import { StarterScreen } from './screens/StarterScreen'
import { ShelterScreen } from './screens/ShelterScreen'
import { InventoryScreen } from './screens/InventoryScreen'
import { AdventureScreen } from './screens/AdventureScreen'
import { PitScreen } from './screens/PitScreen'
import { SettingsScreen } from './screens/SettingsScreen'

export default function App() {
  return (
    <BrowserRouter>
      <GameProvider>
        <AppShell />
      </GameProvider>
    </BrowserRouter>
  )
}

function AppShell() {
  const { status, creature, user } = useGame()
  const unlocked = useRef(false)

  const soundEnabled = user?.settings.soundEnabled ?? true
  const ambienceEnabled = user?.settings.ambienceEnabled ?? true
  const reducedMotion = user?.settings.reducedMotion

  // Remove the pre-bundle boot message once React has painted.
  useEffect(() => {
    document.getElementById('nightbound-boot')?.remove()
  }, [])

  // Audio only ever starts after a real gesture.
  useEffect(() => {
    const unlock = () => {
      if (unlocked.current) return
      unlocked.current = true
      audio.unlock()
      audio.setEnabled(soundEnabled)
      if (soundEnabled && ambienceEnabled) audio.startAmbience()
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
    }
    window.addEventListener('pointerdown', unlock)
    window.addEventListener('keydown', unlock)
    return () => {
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
    }
  }, [soundEnabled, ambienceEnabled])

  useEffect(() => {
    audio.setEnabled(soundEnabled)
    if (soundEnabled && ambienceEnabled && unlocked.current) audio.startAmbience()
    else audio.stopAmbience()
  }, [soundEnabled, ambienceEnabled])

  // Mirror the account's motion preference onto the root element.
  useEffect(() => {
    const root = document.documentElement
    if (reducedMotion === undefined) {
      delete root.dataset.motion
    } else {
      root.dataset.motion = reducedMotion ? 'reduced' : 'auto'
    }
  }, [reducedMotion])

  if (status === 'loading') return <LoadingScreen />

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/" element={<TitleScreen />} />
        <Route path="/enter" element={<AuthScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  if (!creature) {
    return (
      <Routes>
        <Route path="/starter" element={<StarterScreen />} />
        {/* Signed in but no creature yet: nothing to do on /enter. */}
        <Route path="*" element={<Navigate to="/starter" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route element={<GameChrome />}>
        <Route path="/shelter" element={<ShelterScreen />} />
        <Route path="/inventory" element={<InventoryScreen />} />
        <Route path="/adventure" element={<AdventureScreen />} />
        <Route path="/pit" element={<PitScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
      </Route>
      <Route path="/" element={<Navigate to="/shelter" replace />} />
      <Route path="/enter" element={<Navigate to="/shelter" replace />} />
      <Route path="/starter" element={<Navigate to="/shelter" replace />} />
      <Route path="*" element={<Navigate to="/shelter" replace />} />
    </Routes>
  )
}

function GameChrome() {
  return (
    <div className="relative flex min-h-[100svh] flex-col">
      <a
        href="#main"
        className="sr-only rounded-[3px] border border-ember-600 bg-night-900 px-3 py-2 text-xs text-bone-200 focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50"
      >
        Skip to content
      </a>
      <TopNav />
      <main id="main" className="flex-1 pb-24 lg:pb-0">
        <Outlet />
      </main>
      <BottomNav />
      <ToastStack />
      <ErrorBanner />
      <DebugPanel />
    </div>
  )
}
